/* ============================================================
   Live data bootstrap — fills window.WidoData from the backend.

   The design modules captured a reference to `window.WidoData` at load time
   (`const D = window.WidoData`), so we MUTATE that object in place rather than
   replacing it: same reference, live contents. The pure helpers, constants and
   REQ_TYPES from the mock data.js are reused as-is (client-side computation that
   mirrors the backend).

   Call loadAndApply() AFTER a successful login and BEFORE rendering the authed
   screens. Returns the live "me" person.
   ============================================================ */
import { api } from "./client.js";

// Current period follows the real calendar month so every screen rolls over on
// its own (July 1 -> "July 2026"), instead of being frozen at the demo's June.
// The backend honours whatever year/month we send (_ym uses the query args), so
// asking for the live month is all it takes. Mock mode keeps data.js's frozen
// June — this only applies in API mode, where these constants are the anchor.
const _now = new Date();
const YEAR = _now.getFullYear();
const MONTH = _now.getMonth() + 1;

// Two-phase bootstrap so login isn't blocked on the whole dataset:
//   loadCritical() — the handful of fast calls the home dashboard + nav need to
//                    paint (me, settings, my month, roles/teams/permissions,
//                    setup status). Awaited before we drop the user into the app.
//   loadDeferred() — the heavy company-wide calls (roster, everyone's month,
//                    approvals, notifications, devices, policies). Run in the
//                    background after first paint; secondary screens + badges
//                    fill in when it resolves.
// loadAndApply() runs both in sequence (used by refresh / "View as" / setup,
// where the caller wants the full dataset before continuing).

export async function loadCritical() {
  const D = window.WidoData;

  // One round-trip for the whole critical bundle (see backend /api/bootstrap).
  // Fall back to the 7 individual calls if the aggregate route isn't there yet
  // (frontend deployed slightly ahead of backend during a rollout -> 404). A
  // 401 (not signed in) propagates so auth.js shows the chooser.
  let parts;
  try {
    const b = await api.bootstrap(YEAR, MONTH);
    parts = { me: b.me, teams: b.teams, roles: b.roles, permissions: b.permissions, settings: b.settings, meMonth: b.meMonth, setupStatus: b.setup };
  } catch (e) {
    if (!e || e.status !== 404) throw e;
    const [me, teams, roles, permissions, settings, meMonth, setupStatus] = await Promise.all([
      api.me(),
      api.teams(),
      api.roles(),
      api.permissions(),
      api.settings(),
      api.myMonth(YEAR, MONTH),
      api.setupStatus().catch(() => null),
    ]);
    parts = { me, teams, roles, permissions, settings, meMonth, setupStatus };
  }
  applyCritical(D, parts);
  return D.ME;
}

// Apply the critical bundle to window.WidoData (shared by the aggregate and the
// individual-call fallback so they stay in lockstep).
function applyCritical(D, { me, teams, roles, permissions, settings, meMonth, setupStatus }) {
  // --- reference data (mutate arrays in place to preserve captured refs) ---
  replaceArray(D.TEAMS, teams);
  replaceArray(D.PERMISSIONS, permissions);
  replaceArray(D.ROLES, roles);

  // --- current user ---
  D.ME = { ...me, periods: me.periods || null };

  // --- settings document (live) --- device-mapping data arrives with the
  // deferred load; keep whatever's there (mock terminals on first paint) so the
  // Device Mapping screen still renders if opened before loadDeferred resolves.
  const prev = D.DEFAULT_SETTINGS || {};
  D.DEFAULT_SETTINGS = {
    ...settings,
    devices: prev.devices || [],
    deviceEnrollments: prev.deviceEnrollments || {},
    faceMap: prev.faceMap || {},
  };

  // Authoritative first-time-setup state ({configured, isOwner}). The setup-wizard
  // gate prefers this over the /settings snapshot so it never re-shows the wizard
  // for an already-configured deployment.
  D.SETUP_STATUS = setupStatus;

  // --- live current-period markers ---
  // data.js hardcodes MONTH_LABEL ("June 2026") and DAYS_ELAPSED (8, the demo's
  // frozen "today"). In API mode derive both from the real clock so every
  // screen's month label, "today" highlight and on-time math is live.
  D.MONTH_LABEL = new Date(YEAR, MONTH - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  D.CUR_YEAR = YEAR;
  D.CUR_MONTH = MONTH;
  const today = new Date();
  D.DAYS_ELAPSED = (today.getFullYear() === YEAR && today.getMonth() + 1 === MONTH)
    ? today.getDate()                       // viewing the real current month
    : new Date(YEAR, MONTH, 0).getDate();   // a past month -> whole month elapsed

  // --- my month: records carry raw lateMin/earlyMin/permission/weekend, which is
  //     all computeMonth reads; the extra computed fields are ignored. ---
  D.meMonth = { ...meMonth, leaveDays: meMonth.leaveDays != null ? meMonth.leaveDays : ((meMonth.leave && meMonth.leave.taken) || 0) };

  // Clear the mock collections the deferred load will fill, so nothing stale
  // (demo roster, fake requests/badges/notifications) flashes before it lands.
  replaceArray(D.people, []);
  D.ACTIVE = [];
  D.companyStats = [];
  D.requests = [];
  D.pendingAccounts = [];
  D.missingCount = 0;
  window.notificationsFor = () => [];
  window.__WIDO_ROSTER = [];
}

export async function loadDeferred() {
  const D = window.WidoData;
  // loadCritical has already applied roles + me, so the viewer's permissions are
  // known here. Used to skip calls that would only earn a 403.
  const myPerms = ((D.ROLES || []).find((r) => r.id === (D.ME || {}).roleId) || {}).perms || [];

  const [
    people, policies, notifs, myReqs, pending, decidedReqs, accountsPending, records, roster, deviceData,
    missing,
  ] = await Promise.all([
    api.people(),
    api.policies().catch(() => []),
    api.notifications().catch(() => ({ items: [] })),
    api.myRequests().catch(() => []),
    api.approvals("all").catch(() => []),     // pending I can act on (scoped server-side)
    api.decidedRequests("all").catch(() => []), // decided history I can review ([] if no rights)
    api.pendingAccounts().catch(() => []),    // 403 for non-HR -> []
    api.records("all", "&year=" + YEAR + "&month=" + MONTH).catch(() => ({ stats: [] })), // downgraded to team server-side if needed; pin to the live month so companyStats matches D.CUR_*
    // Full roster for the demo "View as" switcher — dev only. Skipped in
    // production (WIDO_DEV_LOGIN off) so we don't fire a doomed dev-users 404.
    window.WIDO_DEV_LOGIN ? api.devUsers().catch(() => []) : Promise.resolve([]),
    api.devices().catch(() => null),          // face-recognition terminals; null -> fall back to mock
    // Clock fix-up worklist -> the nav badge. Only the count is needed here, so
    // it's skipped entirely for anyone without the permission (who wouldn't see
    // the nav item either) rather than firing a call that can only 403.
    myPerms.includes("attendance.override")
      ? api.missingCases(YEAR, MONTH).catch(() => null)
      : Promise.resolve(null),
  ]);

  // Full roster for the "View as" switcher — /people is RBAC-scoped (an employee
  // only sees themselves), so we keep the unscoped dev-users list separately.
  window.__WIDO_ROSTER = roster || [];

  // --- reference data (mutate arrays in place to preserve captured refs) ---
  const peopleArr = people.map((p) => ({ ...p, periods: p.periods || null }));
  replaceArray(D.people, peopleArr);
  D.ACTIVE = peopleArr.filter((p) => p.status === "active");

  // --- settings: fold in live device-mapping data (or fall back to the demo
  // terminals from data.js when the call fails / 403). ---
  const dev = deviceData || {};
  D.DEFAULT_SETTINGS = {
    ...D.DEFAULT_SETTINGS,
    devices: deviceData ? (dev.devices || []) : (D.DEFAULT_SETTINGS.devices || []),
    deviceEnrollments: deviceData ? (dev.deviceEnrollments || {}) : (D.DEFAULT_SETTINGS.deviceEnrollments || {}),
    faceMap: deviceData ? (dev.faceMap || {}) : (D.DEFAULT_SETTINGS.faceMap || {}),
  };

  // --- company rollup -> companyStats (records included by the backend) ---
  D.companyStats = (records.stats || []).map((s) => ({
    person: { ...s.person, periods: null },
    records: s.records || [],
    lateMinTotal: s.lateMinTotal,
    lateDays: s.lateDays,
    fundTotal: s.fundTotal,
    fundRemaining: s.fundRemaining,
    permTotal: s.permTotal,
    permRemaining: s.permRemaining,
    workMinDeducted: s.workMinDeducted,
    dailyUsed: s.dailyUsed,
    leaveDays: s.leaveDays != null ? s.leaveDays : 0, // CUMULATIVE window annual leave taken (balance)
    monthLeaveDays: s.monthLeaveDays != null ? s.monthLeaveDays : 0, // MONTH-scoped leave (paidLeaveMin)
    offsetMin: s.offsetMin || 0,                      // leave-offset minutes (nets Unpaid; Hours summary)
    offsetDays: s.offsetDays || 0,                    // annual leave the offset cost (balance)
    overtimeMin: s.overtimeMin || 0,                  // weekend OT (Hours summary)
    leaveDates: s.leaveDates || null,                 // real approved-leave ISO dates (calendar)
    leaveOff: s.leaveOff || null,                     // iso -> 'am'|'pm'|'full' leave portion (calendar half-days)
    pendingLeaveDates: s.pendingLeaveDates || null,   // not-yet-approved leave ISO dates (calendar)
    pendingLeaveOff: s.pendingLeaveOff || null,       // iso -> portion for those, so a pending half-day shows as half
    pendingPerms: s.pendingPerms || null,             // not-yet-approved late/early {date,type,mins} (calendar)
    approvedPerms: s.approvedPerms || null,            // approved late/early/hourly-leave {date,type,mins} (calendar)
    overridden: s.overridden,
    hasDaily: s.hasDaily,
  }));

  // --- requests: pending I can act on + decided history I can review + my own,
  // deduped by id (decided drives the Approvals "Decided" tab) ---
  const reqById = {};
  [...(pending || []), ...(decidedReqs || []), ...(myReqs || [])].forEach((r) => { reqById[r.id] = r; });
  D.requests = Object.values(reqById);

  // --- activation queue ---
  D.pendingAccounts = accountsPending || [];

  // --- clock fix-ups still open THIS month -> the "Missing clock records" nav
  // badge. Only the count is kept here; the screen fetches the cases themselves
  // (and any other month) on demand. Non-HR gets 403 -> null -> 0, and the badge
  // is gated on attendance.override anyway.
  D.missingCount = (missing && missing.counts && missing.counts.total) || 0;

  // --- policy --- keep the full VN body/bullets from data.js when the backend
  // payload only carries the headings + English summary.
  if (Array.isArray(policies) && policies[0]) {
    const live = policies[0];
    const mockSections = (D.POLICY && D.POLICY.sections) || [];
    live.sections = (live.sections || []).map((s) => {
      const mock = mockSections.find((m) => m.n === s.n) || {};
      return { ...mock, ...s, body: s.body || mock.body, bullets: s.bullets || mock.bullets };
    });
    D.POLICY = live;
  }

  // --- notifications: override the mock generator with the live feed ---
  const liveNotifs = (notifs.items || []).map(mapNotif);
  window.notificationsFor = () => liveNotifs;

  return D.ME;
}

export async function loadAndApply() {
  await loadCritical();
  return loadDeferred();
}

// Lightweight refresh for after a roster edit (team assignment, role change):
// re-fetch ONLY the people list and re-apply it. Deliberately skips the heavy
// company-wide attendance rollup (records?scope=all) and the other deferred
// calls that loadAndApply/loadDeferred run — a roster edit doesn't change
// anyone's computed attendance, so recomputing it for the whole company (which
// freezes the UI) is wasted work.
export async function refreshRoster() {
  const D = window.WidoData;
  const people = await api.people();
  const peopleArr = people.map((p) => ({ ...p, periods: p.periods || null }));
  replaceArray(D.people, peopleArr);
  D.ACTIVE = peopleArr.filter((p) => p.status === "active");
  return D.people;
}

// Re-login as another person (the "View as" switcher in API mode) and reload.
export async function switchTo(personId) {
  await api.devLogin(personId);
  return loadAndApply();
}

function replaceArray(arr, next) {
  arr.length = 0;
  arr.push(...next);
}

const NOTIF_ICONS = {
  "request.approved": "task_alt", "request.rejected": "cancel", "request.submitted": "outbox",
  "account.activated": "verified", "account.pending": "person_add", "fund.low": "battery_alert",
  "attendance.hourly_leave_return": "directions_walk", "request.work_clocks": "more_time",
};

function mapNotif(n) {
  // Backend Notification.to_dict returns { kind, title, body, link, read, createdAt }.
  return {
    id: n.id,
    unread: n.read === undefined ? (n.unread !== undefined ? n.unread : true) : !n.read,
    title: n.title || "",
    body: n.body || "",
    time: n.createdAt || n.time || "",
    icon: n.icon || NOTIF_ICONS[n.kind] || "notifications",
    color: n.color || (n.kind && n.kind.indexOf("reject") >= 0 ? "var(--md-error)" : "var(--md-primary)"),
    link: n.link || null,
  };
}

export default loadAndApply;
